import React, { useRef, useState } from 'react';
import SignaturePad from './SignaturePad';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Paper,
  IconButton,
  Radio,
  FormGroup,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close'; // Add this import
import Header from './Header';
import { useLanguage } from '../context/LanguageContext';
import LanguageModal from './LanguageModal';
import translations from "../context/translations";
import CheckboxGroup from "../utils/CheckboxGroup";

const gradeOptions = [
  { value: 'Pre-K 2', label: 'Pre-K 2' },
  { value: 'Pre-K 3', label: 'Pre-K 3' },
  { value: 'Pre-K 4', label: 'Pre-K 4' },
  { value: 'Kindergarten', label: 'Kindergarten' },
  { value: '1st Grade', label: '1st Grade' },
  { value: '2nd Grade', label: '2nd Grade' },
  { value: '3rd Grade', label: '3rd Grade' },
  { value: '4th Grade', label: '4th Grade' },
  { value: '5th Grade', label: '5th Grade' },
  { value: '6th Grade', label: '6th Grade' },
  { value: '7th Grade', label: '7th Grade' },
  { value: '8th Grade', label: '8th Grade' },
  { value: '9th Grade', label: '9th Grade' },
  { value: '10th Grade', label: '10th Grade' },
  { value: '11th Grade', label: '11th Grade' },
  { value: '12th Grade', label: '12th Grade' }
];

const WaiverSection = ({
  isRtl,
  hasConfirmed,
  setHasConfirmed,
  t,
  sigCanvas,
  printedName,
  setPrintedName,
  date,
  setDate,
  onSignatureComplete,
  initials,
  setInitials
}) => {
  const handleClear = () => {
    sigCanvas.current.clear();
  };

  const handleSave = () => {
    if (!sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      onSignatureComplete({
        printedName,
        signature: signatureData,
        date
      });
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, my: 3 }}>
      <Typography variant="h6" gutterBottom>{t('liabilityWaiver')}</Typography>

      <Box sx={{ maxHeight: '300px', overflowY: 'auto', mb: 3, p: 2, border: '1px solid #eee' }}>
        <Typography variant="h6" gutterBottom>
          {t('waiverTitle')}
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          {t('waiverSubtitle')}
        </Typography>
        <Typography variant="h6" gutterBottom>
          {t('waiverProgramTitle')}
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          {t('waiverFormTitle')}
        </Typography>

        <Typography variant="body1" paragraph>
          {t('waiverParagraph1')}
        </Typography>

        <Typography variant="body1" paragraph>
          {t('waiverParagraph2')}
        </Typography>

        <Typography variant="body1" paragraph>
          {t('waiverParagraph3')}
        </Typography>

        <Typography variant="body1" paragraph>
          <strong>{t('initialIfYouAgree')}</strong>
          <TextField
            variant="standard"
            value={initials}
            onChange={(e) => setInitials(e.target.value)}
            required
            sx={{
              width: '80px',
              ml: 1,
              '& .MuiInput-input': {
                textAlign: 'center',
                borderBottom: '1px solid black'
              }
            }}
          />
          <Typography variant="caption" sx={{ ml: 1 }}>{t('thisIsRequired')}</Typography>
        </Typography>

        <Typography variant="body1" component="div" paragraph>
          <ul style={{ paddingLeft: '20px', marginTop: 0 }}>
            <li>{t('waiverBullet1')}</li>
            <li>{t('waiverBullet2')}</li>
            <li>{t('waiverBullet3')}</li>
            <li>{t('waiverBullet4')}</li>
          </ul>
        </Typography>

        <Typography variant="body1" paragraph>
          {t('waiverParagraph4')}
        </Typography>

        <Typography variant="body1" paragraph>
          <strong>{t('waiverAuthorizationTitle')}</strong> {t('waiverAuthorizationText')}
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={hasConfirmed}
              onChange={(e) => setHasConfirmed(e.target.checked)}
              required
            />
          }
          label={t('initialAgreement')}
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <SignaturePad
            signatureRef={sigCanvas}
            onClear={handleClear}
            sx={{
              direction: 'ltr', // Keep signature input LTR
              border: isRtl ? '1px solid #eee' : '1px solid #eee' // Example of RTL-aware styling
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('printedName')}
            required
            value={printedName}
            onChange={(e) => setPrintedName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('date')}
            type="date"
            InputLabelProps={{ shrink: true }}
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

const StudentEnrollment = ({ userEmail, enrollmentType, onBack, onProfileClick, lang }) => {
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const { t, currentLanguage, getLanguageName, changeLanguage, isRtl } = useLanguage();
  const [students, setStudents] = useState([{ ...getEmptyStudent(), id: Date.now() }]);
  const [expandedStudentId, setExpandedStudentId] = useState(students[0].id);
  const [currentStep, setCurrentStep] = useState('form');
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const sigCanvas = useRef();
  const [printedName, setPrintedName] = useState('');
  const [date, setDate] = useState('');
  const theme = {
    primary: '#E27A58',
    secondary: '#2D3849',
    background: '#f5f5f5',
  };
  const [selectedHealthConditions, setSelectedHealthConditions] = useState([]);
  const [selectedAllergies, setSelectedAllergies] = useState([]);
  const [initials, setInitials] = useState('');

  const handleStudentChange = (id, field, value) => {
    setStudents(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const handleNestedChange = (id, parentField, field, value) => {
    setStudents(prev =>
      prev.map(s =>
        s.id === id
          ? { ...s, [parentField]: { ...s[parentField], [field]: value } }
          : s
      )
    );
  };

  const toggleExpandStudent = (id) => {
    setExpandedStudentId(prev => (prev === id ? null : id));
  };

  const addStudent = () => {
    const newStudent = { ...getEmptyStudent(), id: Date.now() };
    setStudents([...students, newStudent]);
    setExpandedStudentId(newStudent.id);
  };

  const removeStudent = (id) => {
    const updated = students.filter(s => s.id !== id);
    setStudents(updated);
    if (expandedStudentId === id && updated.length > 0) {
      setExpandedStudentId(updated[0].id);
    }
  };

  const handleSubmitAll = async () => {
    if (!signatureData || !hasConfirmed) {
      alert('Please complete the waiver form');
      return;
    }

    setEnrolling(true);
    try {
      const studentsWithEnrollmentType = students.map(student => {
        // Flatten the health conditions
        const flattenedHealthConditions = {};
        Object.entries(student.healthConditions).forEach(([key, value]) => {
          if (key === 'none' || key === 'otherSpecified') {
            flattenedHealthConditions[key] = value;
          } else if (value && value.items) {
            Object.entries(value.items).forEach(([itemKey, itemValue]) => {
              flattenedHealthConditions[`${key}_${itemKey}`] = itemValue;
            });
          }
        });

        // Flatten the allergies
        const flattenedAllergies = {};
        Object.entries(student.allergies).forEach(([key, value]) => {
          if (key === 'none' || key === 'otherSpecified') {
            flattenedAllergies[key] = value;
          } else if (value && value.items) {
            Object.entries(value.items).forEach(([itemKey, itemValue]) => {
              flattenedAllergies[`${key}_${itemKey}`] = itemValue;
            });
          }
        });

        return {
          ...student,
          enrollment_type: enrollmentType,
          healthConditions: JSON.stringify(flattenedHealthConditions), // Convert to string
          allergies: JSON.stringify(flattenedAllergies), // Convert to string
          restrictions: student.physicalRestrictions
        };
      });

      const response = await fetch("https://me40bh7o7b.execute-api.us-east-1.amazonaws.com/prod/enrollment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          user_email: userEmail,
          students: studentsWithEnrollmentType,
          waiver: signatureData
        })
      });

      if (!response.ok) throw new Error("Enrollment failed");

      alert(t('enrollmentSuccess'));
      setStudents([{ ...getEmptyStudent(), id: Date.now() }]);
      setHasConfirmed(false);
      setSignatureData(null);
      setCurrentStep('form');
      onBack();
    } catch (err) {
      console.error("Enrollment error:", err);
      alert(t('enrollmentError'));
    } finally {
      setEnrolling(false);
    }
  };

  // Add these inside your StudentEnrollment component, before the return statement

  // Helper function to format labels
  const formatLabel = (text) => {
    const result = text.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  // Toggle category expansion
  const toggleCategory = (studentId, section, category) => {
    handleNestedChange(
      studentId,
      section,
      category,
      {
        ...students.find(s => s.id === studentId)[section][category],
        expanded: !students.find(s => s.id === studentId)[section][category].expanded
      }
    );
  };

  // Handle checkbox change
  const handleCheckboxChange = (studentId, section, category, item) => {
    const student = students.find(s => s.id === studentId);
    const updatedItems = {
      ...student[section][category].items,
      [item]: !student[section][category].items[item]
    };

    handleNestedChange(
      studentId,
      section,
      category,
      {
        ...student[section][category],
        items: updatedItems
      }
    );

    // Unset "none" if any checkbox is checked
    if (student[section].none) {
      handleNestedChange(studentId, section, 'none', false);
    }
  };

  // Handle "None of the above"
  const handleNoneChange = (studentId, section) => {
    const student = students.find(s => s.id === studentId);
    const newNoneValue = !student[section].none;

    // Reset all checkboxes if "none" is checked
    if (newNoneValue) {
      const resetSection = { ...student[section] };
      Object.keys(resetSection).forEach(category => {
        if (category !== 'none' && category !== 'otherSpecified' && resetSection[category].items) {
          Object.keys(resetSection[category].items).forEach(item => {
            resetSection[category].items[item] = false;
          });
        }
      });

      handleStudentChange(studentId, section, {
        ...resetSection,
        none: newNoneValue,
        otherSpecified: ''
      });
    } else {
      handleNestedChange(studentId, section, 'none', newNoneValue);
    }
  };

  // Handle "Other" text input
  const handleOtherChange = (studentId, section, value) => {
    handleNestedChange(studentId, section, 'otherSpecified', value);
    if (value && students.find(s => s.id === studentId)[section].none) {
      handleNestedChange(studentId, section, 'none', false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: theme.background, direction: isRtl ? 'rtl' : 'ltr' }}>
      {/* Updated Header with language and profile options */}
      <Header
        showProfileIcon={true}
        onProfileClick={onProfileClick}
        onLanguageClick={() => setShowLanguageModal(true)}
        languageLabel={getLanguageName(currentLanguage)}
      />

      {/* Add LanguageModal component */}
      <LanguageModal
        open={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
      />

      <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <Card sx={{
          width: '100%',
          maxWidth: 800,
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 2,
          boxShadow: 3,
          position: 'relative' // For positioning the close button
        }}>
          {/* Add close button to the top-right of the card */}
          <IconButton
            sx={{
              position: 'absolute',
              left: isRtl ? 8 : 'auto', // Use isRtl instead of checking for 'ar'
              right: isRtl ? 'auto' : 8,
              top: 8,
              zIndex: 2,
              color: '#fff'
            }}
            onClick={onBack}
          >
            <CloseIcon />
          </IconButton>
          <CardHeader
            title={<Typography variant="h6" sx={{ color: '#fff' }}>
              {enrollmentType === 'Reading Circle' ? t('readingCircleEnrollment') : t('preschoolCircleEnrollment')}
            </Typography>}
            sx={{
              backgroundColor: theme.primary,
              position: 'relative',
              '& .MuiCardHeader-action': {
                alignSelf: 'center' // Ensures close button is properly aligned
              }
            }}
          />

          <CardContent sx={{
            flexGrow: 1,
            overflowY: 'auto',
            px: 3,
          }}>
            {currentStep === 'form' && (
              <>
                {students.map((student, index) => (
                  <Accordion key={student.id} expanded={expandedStudentId === student.id} onChange={() => toggleExpandStudent(student.id)}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        transform: isRtl ? 'rotate(180deg)' : 'none'
                      }
                    }}
                  >
                      <Typography>
                        {student.firstName || student.lastName ? `${student.firstName} ${student.lastName}` : `${t('student')} ${index + 1}`}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {/* First Name */}
                        <Grid item xs={6}>
                          <TextField
                            label={t('firstName')}
                            fullWidth
                            value={student.firstName}
                            onChange={(e) => handleStudentChange(student.id, 'firstName', e.target.value)}
                            sx={{
                              '& .MuiInputBase-input': {
                                direction: isRtl ? 'rtl' : 'ltr',
                                textAlign: isRtl ? 'right' : 'left'
                              }
                            }}
                          />
                        </Grid>

                        {/* Last Name */}
                        <Grid item xs={6}>
                          <TextField
                            label={t('lastName')}
                            fullWidth
                            value={student.lastName}
                            onChange={(e) => handleStudentChange(student.id, 'lastName', e.target.value)}
                            sx={{
                              '& .MuiInputBase-input': {
                                direction: isRtl ? 'rtl' : 'ltr',
                                textAlign: isRtl ? 'right' : 'left'
                              }
                            }}
                          />
                        </Grid>

                        {/* Date of Birth */}
                        <Grid item xs={6}>
                          <TextField
                            type="date"
                            label={t('dateOfBirth')}
                            fullWidth
                            value={student.dob}
                            onChange={(e) => handleStudentChange(student.id, 'dob', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              '& .MuiInputBase-input': {
                                direction: isRtl ? 'rtl' : 'ltr',
                                textAlign: isRtl ? 'right' : 'left'
                              }
                            }}
                          />
                        </Grid>

                        {/* Gender - Select */}
                        <Grid item xs={6}>
                          <TextField
                            select
                            label={t('gender')}
                            fullWidth
                            value={student.gender}
                            onChange={(e) => handleStudentChange(student.id, 'gender', e.target.value)}
                            sx={{
                              '& .MuiSelect-select': {
                                direction: isRtl ? 'rtl' : 'ltr',
                                textAlign: isRtl ? 'right' : 'left'
                              }
                            }}
                          >
                            <MenuItem value="Male">{t('male')}</MenuItem>
                            <MenuItem value="Female">{t('female')}</MenuItem>
                          </TextField>
                        </Grid>

                        {/* Primary Language */}
                        <Grid item xs={6}>
                          <TextField
                            label={t('primaryLanguage')}
                            fullWidth
                            value={student.language}
                            onChange={(e) => handleStudentChange(student.id, 'language', e.target.value)}
                            sx={{
                              '& .MuiInputBase-input': {
                                direction: isRtl ? 'rtl' : 'ltr',
                                textAlign: isRtl ? 'right' : 'left'
                              }
                            }}
                          />
                        </Grid>

                        {/* School Name */}
                        <Grid item xs={6}>
                          <TextField
                            label={t('schoolName')}
                            fullWidth
                            value={student.school}
                            onChange={(e) => handleStudentChange(student.id, 'school', e.target.value)}
                            sx={{
                              '& .MuiInputBase-input': {
                                direction: isRtl ? 'rtl' : 'ltr',
                                textAlign: isRtl ? 'right' : 'left'
                              }
                            }}
                          />
                        </Grid>

                        {/* Current Grade - Select */}
                        <Grid item xs={6}>
                          <TextField
                            select
                            label={t('currentGrade')}
                            fullWidth
                            value={student.grade}
                            onChange={(e) => handleStudentChange(student.id, 'grade', e.target.value)}
                            sx={{
                              '& .MuiSelect-select': {
                                direction: isRtl ? 'rtl' : 'ltr',
                                textAlign: isRtl ? 'right' : 'left'
                              }
                            }}
                          >
                            <MenuItem value="">
                              <em>{t('choose')}</em>
                            </MenuItem>
                            {gradeOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        {/* Takes Medications - Select */}
                        <Grid item xs={6}>
                          <TextField
                            select
                            label={t('takesMedications')}
                            fullWidth
                            value={student.medications}
                            onChange={(e) => handleStudentChange(student.id, 'medications', e.target.value)}
                            sx={{
                              '& .MuiSelect-select': {
                                direction: isRtl ? 'rtl' : 'ltr',
                                textAlign: isRtl ? 'right' : 'left'
                              }
                            }}
                          >
                            <MenuItem value="Yes">{t('yes')}</MenuItem>
                            <MenuItem value="No">{t('no')}</MenuItem>
                          </TextField>
                        </Grid>

                        {/* Health Conditions Section */}
                        <Grid item xs={12}>
                          <Typography variant="body2" gutterBottom>{t('healthConditions')}</Typography>
                          <Paper elevation={0} sx={{ p: 2, border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 1 }}>
                            {Object.keys(student.healthConditions)
                              .filter(category => category !== 'none' && category !== 'otherSpecified')
                              .map(category => (
                                <Box key={`health-${student.id}-${category}`} sx={{ mb: 2 }}>
                                  <Accordion
                                    expanded={student.healthConditions[category]?.expanded || false}
                                    onChange={() => toggleCategory(student.id, 'healthConditions', category)}
                                    elevation={0}
                                    sx={{
                                      '&:before': { display: 'none' },
                                      border: '1px solid rgba(0, 0, 0, 0.12)',
                                      borderRadius: '4px !important',
                                      mb: 1
                                    }}
                                  >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                      <Typography sx={{ fontWeight: 'medium' }}>
                                        {t(`healthConditions${category.charAt(0).toUpperCase() + category.slice(1)}`)}
                                      </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      <Grid container spacing={1}>
                                        {student.healthConditions[category]?.items &&
                                          Object.keys(student.healthConditions[category].items).map(item => (
                                            <Grid item xs={12} sm={6} key={`health-${student.id}-${category}-${item}`}>
                                              <FormControlLabel
                                                control={
                                                  <Checkbox
                                                    checked={student.healthConditions[category].items[item] || false}
                                                    onChange={() => handleCheckboxChange(student.id, 'healthConditions', category, item)}
                                                    disabled={student.healthConditions.none}
                                                  />
                                                }
                                                label={t(`healthConditions${item.charAt(0).toUpperCase() + item.slice(1)}`)}
                                              />
                                            </Grid>
                                          ))}
                                      </Grid>
                                    </AccordionDetails>
                                  </Accordion>
                                </Box>
                              ))}

                            {/* None of the above */}
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={student.healthConditions.none}
                                  onChange={() => handleNoneChange(student.id, 'healthConditions')}
                                />
                              }
                              label={t('healthConditionsNone')}
                              sx={{ mt: 1 }}
                            />

                            {/* Other specified */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={student.healthConditions.otherSpecified !== ''}
                                    disabled={student.healthConditions.none}
                                  />
                                }
                                label={`${t('healthConditionsOtherSpecified')}:`}
                              />
                              <TextField
                                size="small"
                                value={student.healthConditions.otherSpecified}
                                onChange={(e) => handleOtherChange(student.id, 'healthConditions', e.target.value)}
                                disabled={student.healthConditions.none}
                                sx={{ ml: 1, flexGrow: 1 }}
                              />
                            </Box>
                          </Paper>
                        </Grid>

                        {/* Allergies Section */}
                        <Grid item xs={12}>
                          <Typography variant="body2" gutterBottom>{t('allergies')}</Typography>
                          <Paper elevation={0} sx={{ p: 2, border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 1 }}>
                            {Object.keys(student.allergies)
                              .filter(category => category !== 'none' && category !== 'otherSpecified')
                              .map(category => (
                                <Box key={`allergy-${student.id}-${category}`} sx={{ mb: 2 }}>
                                  <Accordion
                                    expanded={student.allergies[category]?.expanded || false}
                                    onChange={() => toggleCategory(student.id, 'allergies', category)}
                                    elevation={0}
                                    sx={{
                                      '&:before': { display: 'none' },
                                      border: '1px solid rgba(0, 0, 0, 0.12)',
                                      borderRadius: '4px !important',
                                      mb: 1
                                    }}
                                  >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                      <Typography sx={{ fontWeight: 'medium' }}>
                                        {t(`allergies${category.charAt(0).toUpperCase() + category.slice(1)}`)}
                                      </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      <Grid container spacing={1}>
                                        {student.allergies[category]?.items &&
                                          Object.keys(student.allergies[category].items).map(item => (
                                            <Grid item xs={12} sm={6} key={`allergy-${student.id}-${category}-${item}`}>
                                              <FormControlLabel
                                                control={
                                                  <Checkbox
                                                    checked={student.allergies[category].items[item] || false}
                                                    onChange={() => handleCheckboxChange(student.id, 'allergies', category, item)}
                                                    disabled={student.allergies.none}
                                                  />
                                                }
                                                label={t(`allergies${item.charAt(0).toUpperCase() + item.slice(1)}`)}
                                              />
                                            </Grid>
                                          ))}
                                      </Grid>
                                    </AccordionDetails>
                                  </Accordion>
                                </Box>
                              ))}

                            {/* None of the above */}
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={student.allergies.none}
                                  onChange={() => handleNoneChange(student.id, 'allergies')}
                                />
                              }
                              label={t('allergiesNone')}
                              sx={{ mt: 1 }}
                            />

                            {/* Other specified */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={student.allergies.otherSpecified !== ''}
                                    disabled={student.allergies.none}
                                  />
                                }
                                label={`${t('allergiesOtherSpecified')}:`}
                              />
                              <TextField
                                size="small"
                                value={student.allergies.otherSpecified}
                                onChange={(e) => handleOtherChange(student.id, 'allergies', e.target.value)}
                                disabled={student.allergies.none}
                                sx={{ ml: 1, flexGrow: 1 }}
                              />
                            </Box>
                          </Paper>
                        </Grid>

                        {/* Physical Restrictions */}
                        <Grid item xs={12}>
                          <Typography variant="body2" gutterBottom>
                            {t('physicalRestrictionsQuestion')}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel
                              control={
                                <Radio
                                  checked={student.physicalRestrictions === 'yes'}
                                  onChange={() => handleStudentChange(student.id, 'physicalRestrictions', 'yes')}
                                  value="yes"
                                />
                              }
                              label={t('yes')}
                            />
                            <FormControlLabel
                              control={
                                <Radio
                                  checked={student.physicalRestrictions === 'no'}
                                  onChange={() => handleStudentChange(student.id, 'physicalRestrictions', 'no')}
                                  value="no"
                                />
                              }
                              label={t('no')}
                              sx={{ ml: 2 }}
                            />
                          </Box>
                        </Grid>

                        {/* Additional Comments - Multiline */}
                        <Grid item xs={12}>
                          <TextField
                            label={t('additionalComments')}
                            multiline
                            fullWidth
                            rows={3}
                            value={student.comments}
                            onChange={(e) => handleStudentChange(student.id, 'comments', e.target.value)}
                            sx={{
                              '& .MuiInputBase-input': {
                                direction: isRtl ? 'rtl' : 'ltr',
                                textAlign: isRtl ? 'right' : 'left'
                              }
                            }}
                          />
                        </Grid>
                      </Grid> {/* This is the missing closing tag for the Grid container */}

                      {students.length > 1 && (
                        <Button
                          color="error"
                          onClick={() => removeStudent(student.id)}
                          sx={{ mt: 2 }}
                        >
                          {t('removeStudent')}
                        </Button>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Button variant="outlined" onClick={addStudent}>{t('addAnotherStudent')}</Button>
                  <Button variant="contained" onClick={() => setCurrentStep('waiver')} sx={{ bgcolor: theme.primary }}>{t('continueToWaiver')}</Button>
                </Box>
              </>
            )}

            {currentStep === 'waiver' && (
              <>
              <WaiverSection
                isRtl={isRtl}
                hasConfirmed={hasConfirmed}
                setHasConfirmed={setHasConfirmed}
                t={t}
                sigCanvas={sigCanvas}
                printedName={printedName}
                setPrintedName={setPrintedName}
                date={date}
                setDate={setDate}
                initials={initials}
                setInitials={setInitials}
                onSignatureComplete={(data) => {
                  setSignatureData(data);
                  setCurrentStep('review');
                }}
              />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (!initials.trim()) {
                        alert('Please enter your initials to acknowledge the waiver.');
                        return;
                      }

                      if (!printedName.trim()) {
                        alert('Please enter your printed name.');
                        return;
                      }

                      if (!date) {
                        alert('Please select a date.');
                        return;
                      }

                      if (!hasConfirmed) {
                        alert('Please confirm agreement to the waiver.');
                        return;
                      }

                      if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
                        const signatureData = sigCanvas.current.toDataURL();
                        setSignatureData({
                          printedName,
                          signature: signatureData,
                          date
                        });
                        setCurrentStep('review');
                      } else {
                        alert('Please provide your signature.');
                      }
                    }}
                  >
                    {t('continueToReview')}
                  </Button>
                </Box>
              </>
            )}


            {currentStep === 'review' && (
              <>
                <Typography variant="h6" gutterBottom>{t('reviewStudentInfo')}</Typography>
                {students.map((student, index) => (
                  <Paper key={student.id} sx={{ p: 2, my: 2 }}>
                    <Typography variant="subtitle1">{student.firstName} {student.lastName}</Typography>
                    <Typography variant="body2">{t('dateOfBirth')}: {student.dob}</Typography>
                    <Typography variant="body2">{t('gender')}: {t(student.gender === 'Male' ? 'male' : 'female')}</Typography>
                    <Typography variant="body2">{t('schoolName')}: {student.school}</Typography>
                    <Typography variant="body2">{t('currentGrade')}: {student.grade}</Typography>
                    <Typography variant="body2">{t('takesMedications')}: {t(student.medications.toLowerCase())}</Typography>

                    {/* Health Conditions */}
                    <Typography variant="body2" sx={{ mt: 1 }}>{t('healthConditions')}:</Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      {Object.entries(student.healthConditions).flatMap(([category, value]) => {
                        if (category === 'none' && value) {
                          return <li key="none">{t('healthConditionsNone')}</li>;
                        }
                        if (category === 'otherSpecified' && value) {
                          return <li key="other">{t('healthConditionsOtherSpecified')}: {value}</li>;
                        }
                        if (value && value.items) {
                          return Object.entries(value.items)
                            .filter(([_, checked]) => checked)
                            .map(([item]) => (
                              <li key={`${category}-${item}`}>
                                {t(`healthConditions${category.charAt(0).toUpperCase() + category.slice(1)}`)}: {t(`healthConditions${item.charAt(0).toUpperCase() + item.slice(1)}`)}
                              </li>
                            ));
                        }
                        return null;
                      }).filter(Boolean)}
                    </Box>

                    {/* Allergies */}
                    <Typography variant="body2" sx={{ mt: 1 }}>{t('allergies')}:</Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      {Object.entries(student.allergies).flatMap(([category, value]) => {
                        if (category === 'none' && value) {
                          return <li key="none">{t('allergiesNone')}</li>;
                        }
                        if (category === 'otherSpecified' && value) {
                          return <li key="other">{t('allergiesOtherSpecified')}: {value}</li>;
                        }
                        if (value && value.items) {
                          return Object.entries(value.items)
                            .filter(([_, checked]) => checked)
                            .map(([item]) => (
                              <li key={`${category}-${item}`}>
                                {t(`allergies${category.charAt(0).toUpperCase() + category.slice(1)}`)}: {t(`allergies${item.charAt(0).toUpperCase() + item.slice(1)}`)}
                              </li>
                            ));
                        }
                        return null;
                      }).filter(Boolean)}
                    </Box>

                    {/* Physical Restrictions */}
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {t('physicalRestrictions')}: {student.physicalRestrictions === 'yes' ? t('yes') : t('no')}
                    </Typography>

                    {/* Comments */}
                    {student.comments && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {t('additionalComments')}: {student.comments}
                      </Typography>
                    )}

                    <Button
                      onClick={() => { setCurrentStep('form'); setExpandedStudentId(student.id); }}
                      sx={{ mt: 1 }}
                    >
                      {t('edit')}
                    </Button>
                  </Paper>
                ))}

                <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(30, 174, 184, 0.1)', mt: 3 }}>
                  <FormControlLabel
                    control={<Checkbox checked={hasConfirmed} onChange={(e) => setHasConfirmed(e.target.checked)} color="primary" />}
                    label={t('iAcknowledge')}
                  />
                </Paper>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Button variant="outlined" onClick={() => setCurrentStep('form')}>{t('back')}</Button>
                  <Button variant="contained" disabled={!hasConfirmed || enrolling} onClick={handleSubmitAll} sx={{ bgcolor: theme.primary }}>{t('submitAll')}</Button>
                </Box>
                <Paper sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle1">Waiver Signature</Typography>
                  <Typography>Name: {signatureData?.printedName}</Typography>
                  <Typography>Date: {signatureData?.date}</Typography>
                  {signatureData?.signature && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">Signature:</Typography>
                      <img
                        src={signatureData.signature}
                        alt="Signature"
                        style={{ maxWidth: '300px', border: '1px solid #eee' }}
                      />
                    </Box>
                  )}
                </Paper>
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ p: 2, bgcolor: '#1EAEB8', color: '#fff', textAlign: 'center', mt: 'auto' }}>
        <Typography variant="body2">{t('footerText')}</Typography>
      </Box>
    </Box>
  );
};

const getEmptyStudent = () => ({
  firstName: '',
  lastName: '',
  dob: '',
  gender: '',
  language: '',
  school: '',
  grade: '',
  medications: '',
  healthConditions: {
    respiratory: { expanded: false, items: { asthma: false, cysticFibrosis: false } },
    neurological: { expanded: false, items: { adhd: false, asd: false, seizures: false, convulsions: false } },
    mentalHealth: { expanded: false, items: { anxiety: false, depression: false } },
    sensory: { expanded: false, items: { hearingImpairment: false, visionImpairment: false } },
    other: { expanded: false, items: { diabetes: false, heartDisease: false, bleedingClottingDisorder: false } },
    none: false,
    otherSpecified: ''
  },
  allergies: {
    food: { expanded: false, items: { peanuts: false, treeNuts: false, shellfish: false, wheat: false, soy: false, eggs: false, milk: false, sesame: false } },
    environmental: { expanded: false, items: { pollen: false, poisonIvy: false, insectStings: false } },
    medical: { expanded: false, items: { penicillin: false, latex: false } },
    none: false,
    otherSpecified: ''
  },
  physicalRestrictions: '',
  comments: ''
});

export default StudentEnrollment;
